import React, { memo, useCallback } from 'react';
import Lottie from 'react-lottie';
import { Tooltip } from 'components';
import StatusIndicator from 'components/StatusIndicator';
import Comment from '../icons/Comment.png';
import Dot from '../icons/Oval.png';
import Team from '../icons/Team.png';
import { UserValue } from 'types';
import yellowStar from 'static/animations/yellowStar.json';
import {
  StyledCellContainer,
  StyledCellContent,
  StyledHighlight,
  StyledSelectionHighlight,
} from './styled';
import { useSelector } from 'react-redux';
import { modalSelector } from 'store/selectors/modal';
import { useTranslation } from 'react-i18next';
import { getZoomedValue, hasCommunityOrGroupAdminPermissions } from 'utils';
import { NoResponse } from 'static';
import { getCurrentCommunity } from 'store/selectors/currentCommunity';
import { getCurrentUserInCommunity } from 'store/selectors/currentCommunity';
import { getEventResponses } from 'store/selectors/eventResponses';

interface CellProps {
  style: { [key: string]: string | number };
  zoom: number;
  isCurrentUser: boolean;
  cell: UserValue;
  divide?: 'left' | 'right';
  eventId?: string;
  userId?: string;
  cellType?: 'task' | 'event';
  isPast?: boolean;
}

const Cell = memo<CellProps>(
  ({
    style,
    cell = {},
    zoom,
    isCurrentUser,
    divide,
    eventId,
    userId,
    cellType = 'event',
    isPast,
  }) => {
    const { t } = useTranslation();
    const community = useSelector(getCurrentCommunity);
    const currentUser = useSelector(getCurrentUserInCommunity);
    const eventResponses = useSelector(getEventResponses);
    const hasVisibleNoResponse = useCallback(
      () => hasCommunityOrGroupAdminPermissions(currentUser.id, community),
      [currentUser.id, community],
    );
    const cellData = cell || ({} as UserValue);
    const { color, guests, comment, commentText, star, dot, text, onClick } =
      cellData;
    const { data, isOpen: communityModalOpen } = useSelector(modalSelector);
    const getStatusIndicator = () => {
      if (
        cellType === 'task' &&
        !color &&
        eventId !== 'overAllPlus' &&
        eventId !== 'groupPlus' &&
        hasVisibleNoResponse()
      ) {
        return <StatusIndicator type="task-inactive" zoom={zoom} />;
      }

      if (
        cellType === 'event' &&
        !color &&
        eventId !== 'overAllPlus' &&
        eventId !== 'groupPlus' &&
        hasVisibleNoResponse()
      ) {
        return <StatusIndicator type="not-activated" zoom={zoom} />;
      }
      if (
        color === '#8298AB' &&
        eventId !== 'overAllPlus' &&
        eventId !== 'groupPlus'
      ) {
        return (
          <StatusIndicator
            showAction={userId === currentUser.id}
            type="awaiting-reply"
            zoom={zoom}
          />
        );
      }
      return null;
    };

    const getResponseTitleByColor = () => {
      if (!color) return null;

      // Find matching response by color
      const matchingResponse = eventResponses.find(
        (response) => response.color === color,
      );

      if (matchingResponse) {
        return matchingResponse.title;
      }

      return null;
    };

    return (
      <div style={style} id={eventId}>
        {!!isCurrentUser && <StyledHighlight zoom={zoom} />}
        {communityModalOpen &&
          data?.['eventId']?.toString() === eventId?.toString() && (
            <StyledSelectionHighlight zoom={zoom} />
          )}
        {communityModalOpen &&
          userId &&
          data?.['userId']?.toString() === userId && (
            <StyledHighlight zoom={zoom} size={5} />
          )}
        <StyledCellContainer
          zoom={zoom}
          clickable={!!onClick}
          divide={divide}
          isCurrentUser={isCurrentUser}
          onClick={onClick}
          isPast={isPast}
        >
          <StyledCellContent zoom={zoom} color={color}>
            {getResponseTitleByColor() && (
              <Tooltip
                text={getResponseTitleByColor()}
                zoom={zoom}
                isVisible
                opacity="1"
                tooltipStyle={{
                  whiteSpace: 'pre-wrap',
                  maxWidth: 200 * zoom + 'px',
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  height: 'auto',
                  color: color || '#8298ab',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1,
                  }}
                />
              </Tooltip>
            )}
            {getStatusIndicator()}
            {!!guests && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <img className="gumb-grid-cell-icon" src={Team} alt="team" />
                {guests > 1 && (
                  <span style={{ marginLeft: '1px' }}>{`+${guests}`}</span>
                )}
              </div>
            )}
            {!!star && (
              <Tooltip
                isVisible
                text={t('event.nominated')}
                zoom={zoom}
                opacity="1"
                offset={6 * zoom}
                tooltipStyle={{
                  whiteSpace: 'pre-wrap',
                  maxWidth: 200 * zoom + 'px',
                  height: 'auto',
                }}
              >
                <div
                  style={{
                    width: `${getZoomedValue(20, zoom, 'px')}`,
                    height: `${getZoomedValue(20, zoom, 'px')}`,
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      width: `${getZoomedValue(36, zoom, 'px')}`,
                      height: `${getZoomedValue(36, zoom, 'px')}`,
                      marginLeft: `${getZoomedValue(-8, zoom, 'px')}`,
                      marginTop: `${getZoomedValue(-8, zoom, 'px')}`,
                      zIndex: 1,
                    }}
                  />
                  <Lottie
                    options={{
                      animationData: yellowStar,
                      autoplay: true,
                      loop: true,
                    }}
                    width={getZoomedValue(35, zoom, 'px')}
                    height={getZoomedValue(35, zoom, 'px')}
                    style={{
                      position: 'absolute',
                      top: `${getZoomedValue(0, zoom, 'px')}`,
                      transform: 'translateX(-22%)',
                      zIndex: 2,
                    }}
                  />
                </div>
              </Tooltip>
            )}
            {!!dot && (
              <Tooltip
                isVisible
                text={t('event.havePenalty')}
                zoom={zoom}
                opacity="1"
                tooltipStyle={{
                  whiteSpace: 'pre-wrap',
                  maxWidth: 200 * zoom + 'px',
                  height: 'auto',
                }}
              >
                <div
                  style={{ position: 'relative', zIndex: 2, margin: '0 2px' }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      left: '-8px',
                      right: '-8px',
                      bottom: '-8px',
                      zIndex: 1,
                    }}
                  />
                  <img
                    src={Dot}
                    alt="dot"
                    width={12 * zoom}
                    height={12 * zoom}
                  />
                </div>
              </Tooltip>
            )}
            {!!comment && (
              <Tooltip
                isVisible
                text={commentText}
                zoom={zoom}
                opacity="1"
                tooltipStyle={{
                  whiteSpace: 'pre-wrap',
                  maxWidth: 200 * zoom + 'px',
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  height: 'auto',
                  color: color || '#8298ab',
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4px',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      left: '-8px',
                      right: '-8px',
                      bottom: '-8px',
                      zIndex: 1,
                    }}
                  />
                  <img
                    src={Comment}
                    alt="comment"
                    width={14 * zoom}
                    height={14 * zoom}
                  />
                </div>
              </Tooltip>
            )}
            {color === '#8298AB' &&
              eventId !== 'overAllPlus' &&
              eventId !== 'groupPlus' && <NoResponse />}
            {!!text && <span className="gumb-grid-cell-text">{text}</span>}
          </StyledCellContent>
        </StyledCellContainer>
      </div>
    );
  },
);

Cell.displayName = 'Cell';

export default Cell;
